import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Upload, X, ShieldCheck, Clock, FileCheck2, AlertTriangle, Check } from "lucide-react";
import SEO, { SITE_URL } from "../components/SEO";
import { PageHero } from "./Services";
import WarrantyBadge from "../components/WarrantyBadge";
import { brand, afterRoof, warranty } from "../data/site";

const ENDPOINT = "/api/claim";
const MAX_FILES = 6;
const MAX_TOTAL_BYTES = 4 * 1024 * 1024;
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif";

const TERM = warranty.laborYears || "25";

// These are commitments printed in the warranty certificate, not marketing
// copy. If the certificate changes, change them here too.
const PROMISE = [
  { icon: Clock, t: "1 business day", d: "We acknowledge your claim in writing." },
  { icon: ShieldCheck, t: "2 business days", d: "An inspection is on the calendar — same day if water is actively entering the building." },
  { icon: FileCheck2, t: "3 business days", d: "After the inspection you get a written determination with photographs." },
];

const COVERED = [
  "Leaks caused by how we installed the roof",
  "Flashing, valley metal and penetrations we installed",
  "Fasteners, laps and details that were our workmanship",
  "Labor and materials to put it right — no deductible, no proration",
];
const NOT_COVERED = [
  "Damage from a storm, impact or anything that hit the roof",
  "Work another contractor performed or altered",
  "Failure of a material covered by the manufacturer's own warranty",
  "Damage from lack of maintenance, foot traffic or added equipment",
];

const FAQ = [
  {
    q: `How long is the Pepla workmanship warranty?`,
    a: `${TERM} years from the date of substantial completion, non-prorated and transferable. Year ${TERM} is covered exactly as fully as year one, and there is no deductible.`,
  },
  {
    q: "What does it cost to file a warranty claim?",
    a: "Nothing. The inspection, the written determination and the repair are all free when the cause is our workmanship. If the inspection shows the cause is something else, you still get the written determination and the photographs at no charge.",
  },
  {
    q: "How fast will someone come out?",
    a: "We acknowledge your claim in writing within one business day and have an inspection on the calendar within two. If water is actively entering the building we aim for the same day.",
  },
  {
    q: "Does the warranty transfer if I sell the house?",
    a: `Yes. The workmanship warranty transfers with the property for the remainder of the ${TERM}-year term, at no cost and with no paperwork fee. A transferable warranty with years left on it is a documented asset at resale.`,
  },
  {
    q: "What if Pepla did not install my roof?",
    a: "The workmanship warranty only covers roofs we installed. If another contractor did the work we can still inspect it and tell you in writing what we find — that is a free inspection, not a warranty claim.",
  },
  {
    q: "Does a repair restart the clock?",
    a: "No. Repairs are covered for the remainder of the original term, and the term runs from substantial completion of the original installation.",
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Warranty Claim", item: `${SITE_URL}/warranty-claim` },
      ],
    },
  ],
};

const readAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || "");
      const i = s.indexOf(",");
      i === -1 ? reject(new Error("unreadable")) : resolve(s.slice(i + 1));
    };
    r.onerror = () => reject(new Error("unreadable"));
    r.readAsDataURL(file);
  });

const Field = ({ label, children, required }) => (
  <label className="block">
    <span className="block text-xs font-medium uppercase tracking-wide text-ink/55 mb-1.5">
      {label}
      {required && <span className="text-brand-dark ml-0.5" aria-hidden="true">*</span>}
    </span>
    {children}
  </label>
);

const inputCls =
  "w-full h-11 px-3 rounded-lg border border-ink/15 bg-white text-ink " +
  "focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand";

const Radio = ({ name, value, checked, onChange, children }) => (
  <label className={`cursor-pointer text-sm rounded-lg border px-3 py-2 transition-colors ${
    checked ? "border-brand bg-brand/8 text-ink font-medium" : "border-ink/15 text-ink/70 hover:border-brand/50"}`}>
    <input type="radio" name={name} value={value} checked={checked} onChange={onChange} className="sr-only" />
    {children}
  </label>
);

const WarrantyClaim = () => {
  const [form, setForm] = useState({
    firstName: "", lastName: "", phone: "", email: "",
    street: "", city: "", zip: "",
    installYear: "", peplaInstalled: "", roofType: "",
    duringStorm: "", firstNoticed: "", interiorDamage: "", activeLeak: "",
    description: "", company: "",
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const pick = (k, v) => () => setForm((f) => ({ ...f, [k]: v }));

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    setFiles((cur) => {
      const next = [...cur];
      for (const f of incoming) {
        if (next.length >= MAX_FILES) break;
        if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
        next.push(f);
      }
      return next;
    });
  };
  const totalBytes = files.reduce((n, f) => n + f.size, 0);
  const tooBig = totalBytes > MAX_TOTAL_BYTES;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (tooBig) { setError("Those photos add up to more than 4 MB. Remove one and try again."); return; }
    setSubmitting(true);
    try {
      const attachments = [];
      for (const f of files) {
        attachments.push({ filename: f.name, content_type: f.type || "", content: await readAsBase64(f) });
      }
      const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, attachments, page: window.location.pathname }),
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok || !out.ok) throw new Error(out.error || "Something went wrong.");
      setDone({ rejected: out.rejected || 0, urgent: form.activeLeak === "Yes" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err.message || "Something went wrong. Please call (602) 834-4707.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <SEO
        title={`Warranty Claim | ${TERM}-Year Workmanship Warranty | Pepla Roofing`}
        description={`File a warranty claim on your Pepla Roofing installation. Acknowledged in writing within one business day, inspection within two, written determination within three. ${TERM}-year non-prorated workmanship warranty.`}
        jsonLd={jsonLd}
      />

      <PageHero
        eyebrow="Warranty Claim Center"
        title="Something Wrong? Tell Us."
        subtitle={`Your workmanship warranty runs ${TERM} years, non-prorated, with no deductible. Filing a claim costs nothing and the inspection is free either way.`}
        image={afterRoof}
        imageAlt="Arizona tile roof under warranty by Pepla Roofing"
        badge={<WarrantyBadge size={136} eager className="w-28 md:w-[136px]" />}
      />

      {/* Active leak — this is the one thing that should not go through a form */}
      <section className="bg-brand text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
          <AlertTriangle size={22} className="shrink-0" aria-hidden="true" />
          <p className="text-sm flex-1">
            <b>Water coming in right now?</b> Do not wait on a form. Call us and we will get someone out the same day.
          </p>
          <a
            href={`tel:${brand.phoneRaw}`}
            className="inline-flex items-center justify-center gap-2 bg-white text-ink font-semibold text-sm px-5 py-2.5 rounded-md hover:bg-white/90 transition-colors whitespace-nowrap"
          >
            <Phone size={16} /> (602) 834-4707
          </a>
        </div>
      </section>

      {/* What happens after you hit send */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="kicker">What Happens Next</span>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">
              Three Commitments, In Writing
            </h2>
            <div className="brand-line mb-5" />
            <p className="text-muted-foreground text-lg">
              These are not service goals. They are printed in the warranty certificate you were issued, which
              means you can hold us to them.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PROMISE.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.t} className="card-lift bg-card border border-ink/10 rounded-2xl p-7">
                  <Icon size={26} className="text-brand mb-4" aria-hidden="true" />
                  <h3 className="font-heading text-2xl uppercase tracking-wide mb-2">{p.t}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{p.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* The form */}
      <section id="file" className="py-20 bg-secondary border-y border-ink/10">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          {done ? (
            <div className="bg-card border border-ink/10 rounded-2xl p-8 md:p-10 text-center">
              <span className="w-14 h-14 rounded-full bg-brand/12 flex items-center justify-center mx-auto mb-5">
                <Check size={28} className="text-brand-dark" aria-hidden="true" />
              </span>
              <h2 className="font-heading text-3xl uppercase tracking-tight mb-3">Claim Received</h2>
              <div className="brand-line mx-auto mb-5" />
              <p className="text-muted-foreground mb-2">
                You will have written acknowledgment within one business day and an inspection on the calendar
                within two.
                {done.urgent && " You told us water is entering the building, so we will aim for today — if you have not heard from us within the hour, call."}
              </p>
              {done.rejected > 0 && (
                <p className="text-sm text-brand-dark mt-4">
                  {done.rejected} file{done.rejected === 1 ? " was" : "s were"} not attached — wrong file type or over
                  the size limit. Email them to Contact@PeplaRoofing.com and we will add them to your file.
                </p>
              )}
              <a
                href={`tel:${brand.phoneRaw}`}
                className="inline-flex items-center gap-2 mt-7 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-7 py-3.5 rounded-md transition-colors"
              >
                <Phone size={16} /> (602) 834-4707
              </a>
            </div>
          ) : (
            <form onSubmit={submit} noValidate>
              <div className="mb-10">
                <span className="kicker">File A Claim</span>
                <h2 className="font-heading text-4xl uppercase tracking-tight mt-3 mb-4">Tell Us What You See</h2>
                <div className="brand-line mb-5" />
                <p className="text-muted-foreground">
                  Fill in what you know. If you are unsure about a detail, leave it — we would rather have the
                  claim than a complete form.
                </p>
              </div>

              {/* honeypot */}
              <input
                type="text" name="company" tabIndex={-1} autoComplete="off" aria-hidden="true"
                value={form.company} onChange={set("company")}
                className="absolute opacity-0 w-0 h-0 pointer-events-none"
              />

              <fieldset className="mb-9">
                <legend className="font-heading text-xl uppercase tracking-wide mb-5">You and the property</legend>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="First name" required>
                    <input className={inputCls} value={form.firstName} onChange={set("firstName")} required autoComplete="given-name" />
                  </Field>
                  <Field label="Last name" required>
                    <input className={inputCls} value={form.lastName} onChange={set("lastName")} required autoComplete="family-name" />
                  </Field>
                  <Field label="Phone" required>
                    <input className={inputCls} type="tel" value={form.phone} onChange={set("phone")} required autoComplete="tel" />
                  </Field>
                  <Field label="Email">
                    <input className={inputCls} type="email" value={form.email} onChange={set("email")} autoComplete="email" />
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="Street address" required>
                    <input className={inputCls} value={form.street} onChange={set("street")} required autoComplete="street-address" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                  <Field label="City" required>
                    <input className={inputCls} value={form.city} onChange={set("city")} required autoComplete="address-level2" />
                  </Field>
                  <Field label="ZIP code">
                    <input className={inputCls} value={form.zip} onChange={set("zip")} inputMode="numeric" autoComplete="postal-code" />
                  </Field>
                </div>
              </fieldset>

              <fieldset className="mb-9">
                <legend className="font-heading text-xl uppercase tracking-wide mb-5">The roof</legend>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Year the roof was installed">
                    <input className={inputCls} value={form.installYear} onChange={set("installYear")} placeholder="e.g. 2021" inputMode="numeric" maxLength={4} />
                  </Field>
                  <Field label="Did Pepla install it?">
                    <div className="flex gap-2">
                      {["Yes", "No", "Not sure"].map((v) => (
                        <Radio key={v} name="peplaInstalled" value={v} checked={form.peplaInstalled === v} onChange={pick("peplaInstalled", v)}>{v}</Radio>
                      ))}
                    </div>
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="Roof type">
                    <div className="flex flex-wrap gap-2">
                      {["Tile", "Shingle", "Foam or flat", "Metal", "Not sure"].map((v) => (
                        <Radio key={v} name="roofType" value={v} checked={form.roofType === v} onChange={pick("roofType", v)}>{v}</Radio>
                      ))}
                    </div>
                  </Field>
                </div>
              </fieldset>

              <fieldset className="mb-9">
                <legend className="font-heading text-xl uppercase tracking-wide mb-5">The problem</legend>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Is water entering the building now?">
                    <div className="flex gap-2">
                      {["Yes", "No", "Not sure"].map((v) => (
                        <Radio key={v} name="activeLeak" value={v} checked={form.activeLeak === v} onChange={pick("activeLeak", v)}>{v}</Radio>
                      ))}
                    </div>
                  </Field>
                  <Field label="Any interior damage?">
                    <div className="flex gap-2">
                      {["Yes", "No", "Not sure"].map((v) => (
                        <Radio key={v} name="interiorDamage" value={v} checked={form.interiorDamage === v} onChange={pick("interiorDamage", v)}>{v}</Radio>
                      ))}
                    </div>
                  </Field>
                  <Field label="Did it start during a storm?">
                    <div className="flex gap-2">
                      {["Yes", "No", "Not sure"].map((v) => (
                        <Radio key={v} name="duringStorm" value={v} checked={form.duringStorm === v} onChange={pick("duringStorm", v)}>{v}</Radio>
                      ))}
                    </div>
                  </Field>
                  <Field label="When did you first notice it?">
                    <input className={inputCls} value={form.firstNoticed} onChange={set("firstNoticed")} placeholder="e.g. after the storm on the 3rd" />
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="Describe what you are seeing">
                    <textarea
                      className="w-full min-h-[140px] p-3 rounded-lg border border-ink/15 bg-white text-ink focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand"
                      value={form.description} onChange={set("description")}
                      placeholder="Where the stain is, when it appears, whether it only shows up in wind-driven rain…"
                    />
                  </Field>
                  <p className="text-xs text-ink/50 mt-2">
                    A leak that only shows in a driving rain and not a steady one usually points at the
                    underlayment rather than a broken tile. Details like that speed up the inspection.
                  </p>
                </div>
              </fieldset>

              <fieldset className="mb-9">
                <legend className="font-heading text-xl uppercase tracking-wide mb-5">Photos</legend>
                <p className="text-muted-foreground text-sm mb-4">
                  Photographs of the stain, the ceiling and the roof area if you can see it safely.{" "}
                  <b>Do not get on the roof.</b> Up to {MAX_FILES} files, 4 MB total.
                </p>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full border-2 border-dashed border-ink/20 rounded-xl py-8 flex flex-col items-center gap-2 text-ink/60 hover:border-brand hover:text-brand transition-colors focus:outline-none focus:ring-2 focus:ring-brand"
                >
                  <Upload size={22} aria-hidden="true" />
                  <span className="text-sm font-medium">Choose photos</span>
                </button>
                <input
                  ref={fileRef} type="file" multiple accept={ACCEPT} className="sr-only"
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                />
                {files.length > 0 && (
                  <ul className="mt-4 space-y-2">
                    {files.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-3 bg-white border border-ink/10 rounded-lg px-3 py-2">
                        <span className="text-sm text-ink truncate">{f.name}</span>
                        <span className="flex items-center gap-3 shrink-0">
                          <span className="text-xs text-ink/45">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                          <button
                            type="button" aria-label={`Remove ${f.name}`}
                            onClick={() => setFiles((c) => c.filter((_, j) => j !== i))}
                            className="text-ink/40 hover:text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand rounded"
                          >
                            <X size={16} />
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {tooBig && (
                  <p className="text-sm text-brand-dark mt-3">
                    That is {(totalBytes / 1024 / 1024).toFixed(1)} MB — over the 4 MB limit. Remove one and
                    email the rest to Contact@PeplaRoofing.com.
                  </p>
                )}
              </fieldset>

              {error && (
                <p role="alert" className="mb-5 text-sm text-brand-dark bg-brand/8 border border-brand/25 rounded-lg px-4 py-3">
                  {error}
                </p>
              )}

              <p className="text-xs text-ink/50 mb-5">
                Filing a claim starts a warranty review. Coverage is confirmed after the inspection — we will tell
                you in writing either way, with photographs, whether or not the cause turns out to be our work.
              </p>

              <button
                type="submit" disabled={submitting}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-light disabled:opacity-60 text-ink font-semibold uppercase tracking-wider text-sm px-9 py-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
              >
                {submitting ? "Sending…" : "Submit Warranty Claim"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Coverage */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="kicker">Scope</span>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">
              What The Warranty Covers
            </h2>
            <div className="brand-line mb-5" />
            <p className="text-muted-foreground text-lg">
              The short version. Your certificate is the document that governs, and it lists every exclusion in
              plain language rather than fine print.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-card border border-ink/10 rounded-2xl p-7">
              <h3 className="font-heading text-xl uppercase tracking-wide mb-4">Covered</h3>
              <ul className="space-y-3">
                {COVERED.map((x) => (
                  <li key={x} className="flex gap-3 text-sm text-muted-foreground">
                    <Check size={17} className="text-brand-dark shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-card border border-ink/10 rounded-2xl p-7">
              <h3 className="font-heading text-xl uppercase tracking-wide mb-4">Not covered</h3>
              <ul className="space-y-3">
                {NOT_COVERED.map((x) => (
                  <li key={x} className="flex gap-3 text-sm text-muted-foreground">
                    <X size={17} className="text-ink/35 shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="text-muted-foreground text-sm mt-7">
            Storm damage is not a workmanship claim, but it is often an insurance claim —{" "}
            <Link to="/insurance-claims" className="text-brand-dark underline underline-offset-2">
              we document those too
            </Link>
            .
          </p>
        </div>
      </section>

      {/* FAQ — the AEO surface */}
      <section className="py-20 bg-secondary border-t border-ink/10">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <h2 className="font-heading text-4xl uppercase tracking-tight mb-4">Common Questions</h2>
          <div className="brand-line mb-9" />
          <dl className="divide-y divide-ink/10">
            {FAQ.map((f) => (
              <div key={f.q} className="py-6">
                <dt className="font-heading text-lg uppercase tracking-wide mb-2">{f.q}</dt>
                <dd className="text-muted-foreground leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    </div>
  );
};

export default WarrantyClaim;
